import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { usePlatformConfig } from '../hooks/usePlatformConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const config = usePlatformConfig();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await login(email, password);
            toast.success('¡Bienvenido!');
        } catch (error) {
            console.error('Login error:', error);
            toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
        } finally {
            setLoading(false);
        }
    };

    const logoUrl = config?.logo_login_url ? `${API_URL}${config.logo_login_url}` : null;
    const platformName = config?.nombre_plataforma || 'PaySync';

    return (
        <div className="min-h-screen flex">
            {/* Left side - Background image */}
            <div 
                className="hidden lg:flex lg:w-1/2 xl:w-2/3 relative"
                style={{
                    backgroundImage: `url('https://images.unsplash.com/photo-1767556246687-dc15b262a42e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBibHVlJTIwZ2xhc3N8ZW58MHx8fHwxNzcxMjIyODg1fDA&ixlib=rb-4.1.0&q=85')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div 
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(135deg, rgba(26, 43, 43, 0.95) 0%, rgba(91, 154, 154, 0.85) 100%)'
                    }}
                />
                <div className="relative z-10 flex flex-col justify-center px-12 xl:px-24">
                    <div className="flex items-center gap-3 mb-8">
                        {logoUrl ? (
                            <div className="h-20 flex items-center justify-start overflow-hidden">
                                <img 
                                    src={logoUrl} 
                                    alt={platformName}
                                    className="h-full w-auto object-contain"
                                />
                            </div>
                        ) : (
                            <div className="h-12 px-6 bg-white rounded-xl flex items-center justify-center">
                                <span className="text-slate-900 font-bold text-xl tracking-wide">PaySync</span>
                            </div>
                        )}
                    </div>
                    <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
                        Sistema de Gestión<br />de Cobros
                    </h1>
                    <p className="text-lg text-[#A5D0D0] max-w-md">
                        Centraliza el control de servicios, facturación, pagos y seguimiento de cobros 
                        en una sola plataforma profesional.
                    </p>
                    <div className="mt-12 grid grid-cols-2 gap-6 max-w-md">
                        <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                            <p className="text-3xl font-bold text-white">+500</p>
                            <p className="text-sm text-[#A5D0D0]">Empresas activas</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                            <p className="text-3xl font-bold text-white">99.9%</p>
                            <p className="text-sm text-[#A5D0D0]">Uptime garantizado</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right side - Login form */}
            <div className="w-full lg:w-1/2 xl:w-1/3 flex items-center justify-center p-8 bg-slate-50">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="flex items-center gap-3 mb-8 lg:hidden">
                        {logoUrl ? (
                            <div className="h-14 flex items-center justify-start overflow-hidden">
                                <img 
                                    src={logoUrl} 
                                    alt={platformName}
                                    className="h-full w-auto object-contain"
                                />
                            </div>
                        ) : (
                            <div className="h-10 px-4 bg-[#1A2B2B] rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold tracking-wide">PaySync</span>
                            </div>
                        )}
                    </div>

                    <Card className="shadow-lg border-0">
                        <CardHeader className="space-y-1 pb-6">
                            <CardTitle className="text-2xl font-bold text-slate-900">
                                Iniciar Sesión
                            </CardTitle>
                            <CardDescription className="text-slate-500">
                                Ingresa tus credenciales para acceder al sistema
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-slate-700">
                                        Correo electrónico
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="correo@empresa.cl"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="h-11"
                                        data-testid="login-email"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-slate-700">
                                        Contraseña
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            className="h-11 pr-10"
                                            data-testid="login-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full h-11 bg-[#5B9A9A] hover:bg-[#4A8080] text-white font-medium"
                                    disabled={loading}
                                    data-testid="login-submit"
                                >
                                    {loading ? (
                                        <div className="flex items-center gap-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Ingresando...
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <LogIn size={18} />
                                            Ingresar
                                        </div>
                                    )}
                                </Button>
                            </form>

                            <div className="mt-6 pt-6 border-t border-slate-200">
                                <p className="text-xs text-slate-500 text-center">
                                    ¿Problemas para ingresar? Contacta al administrador de tu empresa
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    <p className="text-center text-xs text-slate-400 mt-6">
                        © 2026 {platformName}. Todos los derechos reservados.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
